import { useEffect, useRef, useState, useCallback } from "react";

const BASE = process.env.REACT_APP_API_URL;

function stripHtml(html = "") {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// ── System prompts ─────────────────────────────────────────────────
function buildSystemPrompt({ pageType, title, content, quizText, quizDebateContext }) {
  const base = `Eres un experto en bioestadística con experiencia docente e investigadora. Actúas como par colaborativo: debatís ideas, cuestionas supuestos y estimulas el pensamiento crítico. Corrige errores con argumentos sólidos. Propón una pregunta de reflexión al final cuando sea útil. Máximo 3 párrafos por respuesta. Responde siempre en español.`;

  if (pageType === "class") {
    const contextParts = [
      content           ? `Contenido de la clase:\n${content.slice(0, 6000)}`  : null,
      quizText          ? `Cuestionario asociado:\n${quizText.slice(0, 2000)}` : null,
      quizDebateContext ? `Respuestas del estudiante:\n${quizDebateContext}`    : null,
    ].filter(Boolean).join("\n\n");

    const debateExtra = quizDebateContext
      ? `\n\nMODO DEBATE — REGLAS ESTRICTAS:
El estudiante acaba de terminar el cuestionario. Tienes su lista de respuestas en el contexto.
Hablale como si estuvieras en vivo con él, corrigiendo sus errores uno por uno, sin apurarte a revelar la respuesta correcta. Usa la segunda persona y un tono socrático, como si estuvieras guiándolo a descubrir por sí mismo dónde se equivocó y cuál es el concepto clave detrás de cada pregunta. Sigue este patrón para cada pregunta fallida:

CÓMO PROCEDER:

1. Estás hablando como si fueras un profesor a un alumno, hablalé de a tú, no hables en primera persona. 
2. Empieza SIEMPRE por la primera pregunta fallida (o sin responder) solamente. No menciones ni enumeres las demás todavía.
3. Pregunta por su razonamiento de forma socrática: ¿por qué eligiste esa opción? ¿qué concepto estabas aplicando? Como cuestionando al que hizo esa respuesta
4. NO reveles la respuesta correcta de inmediato. Guíalo con contra-preguntas hasta que el usuario mismo llegue a la conclusión o admita no saber.
5. Solo cuando el estudiante haya comprendido esa pregunta (lo detectas porque razona bien o pide pasar adelante), introduce la siguiente pregunta fallida con la frase "Bien, pasemos a la siguiente...".
6. Si el estudiante pregunta directamente la respuesta correcta, puedes confirmarla brevemente y luego profundizar en el concepto subyacente.
7. Al final, cuando ya se hayan abordado todas las fallidas, haz un cierre breve con el patrón de errores detectado.
8. NUNCA listes todas las preguntas fallidas juntas en un mismo mensaje.`
      : "";

    const prompt = `${base}

Estás acompañando al estudiante en la clase "${title}". Conoces su contenido completo y puedes hacer referencia a partes específicas.${debateExtra}`;

    return contextParts ? `${prompt}\n\n--- CONTEXTO ---\n${contextParts}` : prompt;
  }

  return base;
}

// ── Mensaje inicial del debate ────────────────────────────────────
function buildDebateOpener(debateAnswers, results) {
  if (!debateAnswers || debateAnswers.length === 0) return null;

  const pct    = Math.round((results.correct / results.total) * 100);
  const wrongs = debateAnswers.filter((a) => !a.wasCorrect && a.userAnswer !== "Sin responder");
  const empty  = debateAnswers.filter((a) => a.userAnswer === "Sin responder");
  const first  = wrongs[0] ?? empty[0];

  // Línea de apertura según puntaje
  let intro = "";
  if (pct === 100) {
    intro = `¡Todo correcto! ${results.correct}/${results.total}. Buen resultado.`;
  } else if (pct >= 60) {
    intro = `Terminaste con ${results.correct}/${results.total} (${pct}%). Hay algunas cosas interesantes que revisar.`;
  } else {
    intro = `Terminaste con ${results.correct}/${results.total} (${pct}%). Hay bastante que podemos trabajar juntos.`;
  }

  // Todo correcto: invita a profundizar
  if (!first) {
    return `${intro}\n\n¿Hubo alguna pregunta donde dudaste antes de marcar? Podemos profundizar en ella aunque hayas acertado.`;
  }

  // Arranca con la primera fallida únicamente, pregunta abierta
  const tipo = first.userAnswer === "Sin responder"
    ? "no respondiste"
    : `respondiste "${first.userAnswer}"`;

  return `${intro}\n\nEmpecemos por la pregunta ${first.index}: "${first.question}"\n\nTú ${tipo}. ¿Qué concepto creías que estaba evaluando esa pregunta?`;
}

// ── Sugerencias por tipo ──────────────────────────────────────────
const SUGGESTIONS = {
  class: [
    "Explícame el concepto principal",
    "Dame un ejemplo clínico",
    "¿Cómo se aplica en investigación?",
    "¿Qué errores cometen los estudiantes aquí?",
  ],
  debate: [
    "¿Por qué me equivoqué en esa pregunta?",
    "Explícame la respuesta correcta",
    "¿Cómo puedo recordar mejor este concepto?",
    "Dame otro ejemplo para entenderlo",
  ],
  article: [
    "¿Qué tema de bioestadística quieres explorar?",
    "Explícame qué es un valor p",
    "¿Cuál es la diferencia entre correlación y causalidad?",
    "Dame un ejemplo de sesgo en investigación",
  ],
  resource: [
    "¿Qué concepto quieres reforzar?",
    "Dame un ejemplo práctico de estadística",
    "¿Cómo interpreto un intervalo de confianza?",
    "¿Qué preguntas podría hacer un examinador?",
  ],
};

const PAGE_LABELS = { class: "Clase", article: "Artículo", resource: "Recurso" };

// ── Hook de chat ──────────────────────────────────────────────────
function useChat(systemPrompt) {
  const [messages,  setMessages]  = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = useCallback(async (text, overrideMessages) => {
    if (!text.trim() || isLoading) return;
    const base = overrideMessages ?? messages;
    const next = [...base, { role: "user", content: text }];
    setMessages(next);
    setIsLoading(true);
    try {
      const res  = await fetch(`${BASE}/chat`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ messages: next, systemPrompt }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error del servidor");
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages([...next, { role: "assistant", content: `Error: ${err.message}` }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, systemPrompt]);

  const resetMessages = useCallback(() => setMessages([]), []);

  return { messages, isLoading, sendMessage, resetMessages };
}

// ══════════════════════════════════════════════════════════════════
export default function ChatWidget({
  pageType      = "class",
  title         = "",
  content       = "",
  quizData      = null,
  // Nuevas props para el modo debate:
  debateAnswers = null,   // array con respuestas del estudiante
  debateResults = null,   // { correct, wrong, empty, total }
  autoOpen      = false,  // si debe abrirse automáticamente
}) {
  const [open,        setOpen]        = useState(false);
  const [input,       setInput]       = useState("");
  const [debateMode,  setDebateMode]  = useState(false);
  const [quizDebateContext, setQuizDebateContext] = useState(null);
  const bottomRef   = useRef(null);
  const didInject   = useRef(false);   // evita doble inyección en StrictMode

  // ── Texto plano del quiz para el system prompt ─────────────────
  const quizText = quizData?.questions
    ? quizData.questions.map((q, i) =>
        `P${i+1}: ${q.question}\n${(Array.isArray(q.options) ? q.options : JSON.parse(q.options)).map((o, j) => `  ${j+1}. ${o}`).join("\n")}`
      ).join("\n\n")
    : null;

  const systemPrompt = buildSystemPrompt({
    pageType,
    title,
    content: stripHtml(content),
    quizText,
    quizDebateContext,
  });

  const { messages, isLoading, sendMessage, resetMessages } = useChat(systemPrompt);

  // ── Cuando llegan nuevas debateAnswers (el usuario apretó el botón) ──
  useEffect(() => {
    if (!debateAnswers || didInject.current) return;
    didInject.current = true;

    // Construye el contexto enriquecido para el system prompt
    const ctx = debateAnswers.map((a) =>
      `P${a.index}: "${a.question}"\n  → Respondió: "${a.userAnswer}" | Correcta: "${a.correct}" | ${a.wasCorrect ? "✓ Correcto" : "✗ Incorrecto"}`
    ).join("\n");
    setQuizDebateContext(ctx);
    setDebateMode(true);

    // Abre el widget si autoOpen
    if (autoOpen) setOpen(true);
  }, [debateAnswers, autoOpen]);

  // ── Cuando el chat se abre en modo debate, inyecta el opener ──
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (!open || !debateMode || messages.length > 0 || prevOpenRef.current) return;
    prevOpenRef.current = true;
    const opener = buildDebateOpener(debateAnswers, debateResults);
    if (opener) {
      // Lo inyectamos como mensaje del asistente (no cuenta como llamada a la API)
      // pero lo enviamos desde el "user" para que el asistente lo responda de forma natural.
      // Mejor: lo ponemos directamente como primer mensaje del asistente.
      resetMessages();
      // Enviamos un mensaje de sistema disfrazado como user para arrancar el debate
      sendMessage(
        `[Contexto interno - no mostrar este mensaje] El estudiante acaba de terminar el cuestionario. Inicia el debate directamente con este mensaje de bienvenida personalizado:\n\n${opener}`
      );
    }
  }, [open, debateMode, debateAnswers, debateResults, messages.length, resetMessages, sendMessage]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, open]);

  const handle = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput("");
  };

  const currentMode    = debateMode ? "debate" : pageType;
  const suggestions    = SUGGESTIONS[currentMode] || SUGGESTIONS.class;
  const headerSubtitle = debateMode
    ? "Modo debate · Reflexiona sobre tus respuestas"
    : pageType === "class"
      ? "Llama 3.3 · 70B · Contexto de clase"
      : "Llama 3.3 · 70B · Par colaborativo";

  return (
    <>
      <style>{`
        @keyframes chatPop { from{opacity:0;transform:scale(.93) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes dot { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} }
        .gc-window { animation: chatPop .18s ease forwards; }
        .gc-msg    { animation: chatPop .13s ease forwards; }
        .gc-bubble { transition: background .2s, transform .15s !important; }
        .gc-bubble:hover { transform: scale(1.08) !important; }
        .gc-sug { transition: background .12s; cursor: pointer; }
        .gc-sug:hover { background: #eef4ff !important; }
      `}</style>

      {/* ── Botón flotante ── */}
      <button
        className="gc-bubble"
        onClick={() => setOpen(v => !v)}
        title={`Asistente · ${PAGE_LABELS[pageType] || "Chat"}`}
        style={{
          position:"fixed", bottom:24, right:24,
          width:50, height:50, borderRadius:"50%",
          background: open ? "#4f46e5" : debateMode ? "#e07b25" : "var(--teal-dark, #0e6b6b)",
          border:"none", cursor:"pointer",
          boxShadow:"0 2px 14px rgba(0,0,0,.26)",
          display:"flex", alignItems:"center", justifyContent:"center",
          zIndex: 1200,
        }}
      >
        {open ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6"  x2="6"  y2="18"/>
            <line x1="6"  y1="6"  x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            <circle cx="9"  cy="10" r=".9" fill="#fff"/>
            <circle cx="12" cy="10" r=".9" fill="#fff"/>
            <circle cx="15" cy="10" r=".9" fill="#fff"/>
          </svg>
        )}
        {/* Indicador de modo debate */}
        {!open && debateMode && (
          <span style={{ position:"absolute", top:3, right:3, width:10, height:10, borderRadius:"50%", background:"#e07b25", border:"2px solid #fff" }}/>
        )}
        {!open && !debateMode && messages.length > 0 && (
          <span style={{ position:"absolute", top:3, right:3, width:10, height:10, borderRadius:"50%", background:"#e53935", border:"2px solid #fff" }}/>
        )}
      </button>

      {/* ── Ventana de chat ── */}
      {open && (
        <div className="gc-window" style={{
          position:"fixed", bottom:82, right:20,
          width:320, height:460, maxHeight:"calc(100vh - 110px)",
          background:"#fff", borderRadius:12,
          boxShadow:"0 8px 32px rgba(0,0,0,.18), 0 1px 4px rgba(0,0,0,.08)",
          display:"flex", flexDirection:"column",
          overflow:"hidden", zIndex:1199, fontFamily:"inherit",
        }}>

          {/* Header */}
          <div style={{
            background: debateMode ? "#c06a18" : "var(--teal-dark, #0e6b6b)",
            padding:"10px 14px", display:"flex", alignItems:"center", gap:9, flexShrink:0
          }}>
            <div style={{ width:32, height:32, borderRadius:"50%", background:"rgba(255,255,255,.18)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>
              {debateMode ? "🗣️" : "🧠"}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ color:"#fff", fontWeight:700, fontSize:"0.82rem", letterSpacing:.3 }}>
                {debateMode ? "Debate · Quiz" : `Asistente · ${PAGE_LABELS[pageType] || "Chat"}`}
              </div>
              <div style={{ color:"rgba(255,255,255,.55)", fontSize:"0.67rem", marginTop:1 }}>
                {headerSubtitle}
              </div>
            </div>
            {/* Botón para salir del modo debate */}
            {debateMode && (
              <button
                onClick={() => {
                  setDebateMode(false);
                  setQuizDebateContext(null);
                  resetMessages();
                  didInject.current   = false;
                  prevOpenRef.current = false;
                }}
                title="Volver al asistente normal"
                style={{ background:"rgba(255,255,255,.15)", border:"none", borderRadius:6, padding:"3px 7px", color:"#fff", fontSize:"0.68rem", cursor:"pointer" }}
              >
                ← Salir
              </button>
            )}
          </div>

          {/* Mensajes */}
          <div style={{ flex:1, overflowY:"auto", padding:"10px 10px 4px", display:"flex", flexDirection:"column", gap:7, background:"#f6f8fa" }}>
            {messages.length === 0 && (
              <div style={{ textAlign:"center", padding:"8px 4px 10px" }}>
                <div style={{ fontSize:24, marginBottom:5 }}>💬</div>
                <p style={{ margin:"0 0 10px", fontWeight:600, fontSize:"0.78rem", color:"#333" }}>
                  {pageType === "class"
                    ? `Debate sobre "${title}"`
                    : "¿En qué te puedo ayudar?"}
                </p>
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  {suggestions.map((s, i) => (
                    <button key={i} className="gc-sug" onClick={() => sendMessage(s)} style={{
                      background:"#fff", border:"0.5px solid #ddd",
                      borderRadius:16, padding:"6px 11px",
                      fontSize:"0.74rem", color:"#444", textAlign:"left",
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {messages
              // Filtra el mensaje de contexto interno que no debe mostrarse
              .filter((m) => !m.content.startsWith("[Contexto interno"))
              .map((m, i) => (
                <div key={i} className="gc-msg" style={{ display:"flex", justifyContent:m.role==="user"?"flex-end":"flex-start" }}>
                  <div style={{
                    maxWidth:"83%", padding:"7px 11px",
                    borderRadius: m.role==="user" ? "13px 13px 3px 13px" : "13px 13px 13px 3px",
                    background:   m.role==="user"
                      ? (debateMode ? "#c06a18" : "var(--teal-dark, #0e6b6b)")
                      : "#fff",
                    color:        m.role==="user" ? "#fff" : "#222",
                    fontSize:"0.79rem", lineHeight:1.6,
                    whiteSpace:"pre-wrap", wordBreak:"break-word",
                    boxShadow: m.role==="assistant" ? "0 1px 3px rgba(0,0,0,.07)" : "none",
                  }}>
                    {m.content}
                  </div>
                </div>
              ))}

            {isLoading && (
              <div style={{ display:"flex" }}>
                <div style={{ background:"#fff", borderRadius:"13px 13px 13px 3px", padding:"9px 13px", display:"flex", gap:4, boxShadow:"0 1px 3px rgba(0,0,0,.07)" }}>
                  {[0,1,2].map(d => (
                    <div key={d} style={{ width:6, height:6, borderRadius:"50%", background: debateMode ? "#c06a18" : "var(--teal-dark, #0e6b6b)", opacity:.7, animation:"dot 1.2s infinite", animationDelay:`${d*.2}s` }}/>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Input */}
          <div style={{ padding:"7px 9px", borderTop:"0.5px solid #e8e8e8", display:"flex", gap:6, alignItems:"flex-end", background:"#fff", flexShrink:0 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); handle(); } }}
              placeholder={debateMode ? "Argumenta tu respuesta..." : "Escribe aquí..."}
              rows={1}
              style={{ flex:1, resize:"none", border:"0.5px solid #ddd", borderRadius:18, padding:"7px 11px", fontSize:"0.78rem", fontFamily:"inherit", color:"#333", background:"#f6f8fa", outline:"none", lineHeight:1.5, maxHeight:70, overflow:"auto" }}
            />
            <button
              onClick={handle}
              disabled={isLoading || !input.trim()}
              style={{ width:32, height:32, borderRadius:"50%", border:"none", background: isLoading||!input.trim() ? "#ccc" : debateMode ? "#c06a18" : "var(--teal-dark, #0e6b6b)", color:"#fff", cursor: isLoading||!input.trim() ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"background .15s" }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}